import { useState } from 'react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  User,
  Package,
  Zap,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import type { Alert, AlertType, AlertStatus } from '../hooks/useAlerts';
import { useAlertDetail } from '../hooks/useAlerts';

interface AlertCardProps {
  alert: Alert;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onAcknowledge?: (id: string) => void;
  onResolve?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

// Alert type display info
const alertTypeInfo: Record<AlertType, { label: string; icon: typeof AlertTriangle; severity: 'critical' | 'warning' | 'info' }> = {
  date_slip: { label: 'Date Slip', icon: Clock, severity: 'critical' },
  stuck_task: { label: 'Stuck Task', icon: AlertCircle, severity: 'warning' },
  capacity_overload: { label: 'Capacity Overload', icon: User, severity: 'warning' },
  skill_gap: { label: 'Skill Gap', icon: User, severity: 'critical' },
  unconfirmed_order: { label: 'Unconfirmed Order', icon: Package, severity: 'info' },
  data_integrity: { label: 'Data Issue', icon: AlertTriangle, severity: 'critical' },
  time_entry_conflict: { label: 'Time Entry Conflict', icon: Clock, severity: 'warning' },
  pin_priority_conflict: { label: 'Pin Conflict', icon: Zap, severity: 'warning' },
  partial_staged: { label: 'Partial Staged', icon: Package, severity: 'info' },
  explosion_review_required: { label: 'BOM Review Required', icon: AlertTriangle, severity: 'critical' },
  memory_pressure: { label: 'Memory Pressure', icon: AlertTriangle, severity: 'critical' },
  scheduling_skipped: { label: 'Scheduling Skipped', icon: AlertCircle, severity: 'warning' },
};

const severityStyles = {
  critical: {
    bg: 'bg-danger-subtle',
    border: 'border-danger',
    icon: 'text-danger',
    badge: 'bg-danger text-white',
  },
  warning: {
    bg: 'bg-warning-subtle',
    border: 'border-warning',
    icon: 'text-warning',
    badge: 'bg-warning text-white',
  },
  info: {
    bg: 'bg-primary/5',
    border: 'border-primary/30',
    icon: 'text-primary',
    badge: 'bg-primary text-white',
  },
};

const statusStyles: Record<AlertStatus, { bg: string; text: string }> = {
  active: { bg: 'bg-danger-subtle', text: 'text-danger' },
  acknowledged: { bg: 'bg-warning-subtle', text: 'text-warning-foreground' },
  resolved: { bg: 'bg-success-subtle', text: 'text-success' },
};

/**
 * Helper to safely format a date string, returning null if invalid.
 */
function safeFormatDate(dateStr: unknown): string | null {
  if (typeof dateStr !== 'string') return null;
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return null;
  }
}

/**
 * Safely get a string from payload field.
 */
function payloadStr(payload: Record<string, unknown> | null, key: string): string | null {
  if (!payload) return null;
  const val = payload[key];
  return typeof val === 'string' ? val : null;
}

/**
 * Safely get a number from payload field.
 */
function payloadNum(payload: Record<string, unknown> | null, key: string): number | null {
  if (!payload) return null;
  const val = payload[key];
  return typeof val === 'number' ? val : null;
}

/**
 * Generate a human-readable summary message based on alert type and payload.
 */
function getAlertMessage(alert: Alert): string {
  const p = alert.payload;

  switch (alert.alertType) {
    case 'date_slip': {
      const slipDays = payloadNum(p, 'slipDays');
      const promised = safeFormatDate(p?.promisedDate);
      const expected = safeFormatDate(p?.expectedDate);
      if (slipDays !== null && promised && expected) {
        return `Delivery slipping ${slipDays} day${slipDays !== 1 ? 's' : ''} late (promised ${promised}, now expected ${expected})`;
      }
      return payloadStr(p, 'message') ?? `Expected date slipped ${slipDays ?? 'N/A'} days past promised`;
    }

    case 'skill_gap': {
      const msg = payloadStr(p, 'message');
      const skillName = payloadStr(p, 'requiredSkillName') ?? payloadStr(p, 'skillName');
      if (skillName) {
        return `No available person with skill "${skillName}"`;
      }
      return msg ?? 'No available person with required skill';
    }

    case 'capacity_overload': {
      const msg = payloadStr(p, 'message');
      const personName = payloadStr(p, 'personName');
      const overloadPercent = payloadNum(p, 'utilizationPercent');
      if (personName && overloadPercent !== null) {
        return `${personName} is at ${overloadPercent}% capacity (overloaded)`;
      }
      return msg ?? 'Person is over capacity';
    }

    case 'pin_priority_conflict': {
      const msg = payloadStr(p, 'message');
      const pinnedPersonName = payloadStr(p, 'pinnedPersonName');
      if (pinnedPersonName) {
        return `Pinned task on ${pinnedPersonName} conflicts with higher-priority work`;
      }
      return msg ?? 'Pinned task conflicts with project priorities';
    }

    case 'scheduling_skipped':
      return payloadStr(p, 'message') ?? 'Project was skipped during scheduling due to system constraints';

    case 'explosion_review_required': {
      const taskCount = payloadNum(p, 'taskCount');
      if (taskCount !== null) {
        return `BOM explosion generated ${taskCount} tasks — review required before continuing`;
      }
      return payloadStr(p, 'message') ?? 'BOM explosion requires review';
    }

    case 'time_entry_conflict':
      return payloadStr(p, 'message') ?? 'Multiple workers attempted to work on the same task simultaneously';

    case 'stuck_task': {
      const msg = payloadStr(p, 'message');
      return msg ?? 'Task appears stuck and may need attention';
    }

    case 'partial_staged': {
      const msg = payloadStr(p, 'message');
      return msg ?? 'Parts are partially staged — some components may be missing';
    }

    default:
      return payloadStr(p, 'message') ?? 'Alert requires attention';
  }
}

/**
 * Get contextual detail lines from the payload for display below the message.
 */
function getAlertContextLines(alert: Alert): { label: string; value: string; link?: string }[] {
  const p = alert.payload;
  const lines: { label: string; value: string; link?: string }[] = [];

  if (!p) return lines;

  // Common: Project ID -> link
  const projectId = payloadStr(p, 'projectId');
  const gpSoNumber = payloadStr(p, 'gpSalesOrderNumber');
  if (gpSoNumber) {
    lines.push({ label: 'Order', value: gpSoNumber, link: projectId ? `/orders/${projectId}` : undefined });
  } else if (projectId) {
    lines.push({ label: 'Project', value: projectId.substring(0, 8) + '...', link: `/orders/${projectId}` });
  }

  switch (alert.alertType) {
    case 'date_slip': {
      const promised = safeFormatDate(p.promisedDate);
      const expected = safeFormatDate(p.expectedDate);
      const slipDays = payloadNum(p, 'slipDays');
      if (promised) lines.push({ label: 'Promised', value: promised });
      if (expected) lines.push({ label: 'Expected', value: expected });
      if (slipDays !== null) lines.push({ label: 'Days Late', value: `${slipDays}` });
      break;
    }

    case 'skill_gap': {
      const skillName = payloadStr(p, 'requiredSkillName') ?? payloadStr(p, 'skillName');
      const laborBucket = payloadStr(p, 'laborBucket');
      if (skillName) lines.push({ label: 'Required Skill', value: skillName });
      if (laborBucket) lines.push({ label: 'Labor Type', value: laborBucket });
      break;
    }

    case 'capacity_overload': {
      const personName = payloadStr(p, 'personName');
      const personId = payloadStr(p, 'personId');
      const utilization = payloadNum(p, 'utilizationPercent');
      const demandMin = payloadNum(p, 'demandMinutes');
      const availMin = payloadNum(p, 'availableMinutes');
      if (personName) lines.push({ label: 'Person', value: personName });
      else if (personId) lines.push({ label: 'Person', value: personId.substring(0, 8) + '...' });
      if (utilization !== null) lines.push({ label: 'Utilization', value: `${utilization}%` });
      if (demandMin !== null && availMin !== null) {
        const overHrs = Math.round((demandMin - availMin) / 60 * 10) / 10;
        if (overHrs > 0) lines.push({ label: 'Over by', value: `${overHrs} hours` });
      }
      break;
    }

    case 'pin_priority_conflict': {
      const pinnedPersonName = payloadStr(p, 'pinnedPersonName');
      const pinnedTaskId = payloadStr(p, 'pinnedTaskId');
      const affectedProjectId = payloadStr(p, 'affectedProjectId');
      if (pinnedPersonName) lines.push({ label: 'Pinned To', value: pinnedPersonName });
      if (pinnedTaskId) lines.push({ label: 'Task', value: pinnedTaskId.substring(0, 8) + '...' });
      if (affectedProjectId) {
        lines.push({ label: 'Affected Project', value: affectedProjectId.substring(0, 8) + '...', link: `/orders/${affectedProjectId}` });
      }
      break;
    }

    case 'explosion_review_required': {
      const taskCount = payloadNum(p, 'taskCount');
      const reason = payloadStr(p, 'reason');
      if (taskCount !== null) lines.push({ label: 'Tasks Generated', value: `${taskCount}` });
      if (reason) lines.push({ label: 'Reason', value: reason === 'safety_brake' ? 'Safety brake triggered' : reason });
      break;
    }

    case 'time_entry_conflict': {
      const personId = payloadStr(p, 'personId');
      const taskId = payloadStr(p, 'taskId');
      if (personId) lines.push({ label: 'Person', value: personId.substring(0, 8) + '...' });
      if (taskId) lines.push({ label: 'Task', value: taskId.substring(0, 8) + '...' });
      break;
    }

    case 'scheduling_skipped': {
      const reason = payloadStr(p, 'reason');
      if (reason) lines.push({ label: 'Reason', value: reason === 'memory_pressure' ? 'Memory pressure' : reason });
      break;
    }
  }

  return lines;
}

/**
 * Expandable detail section that fetches full alert detail from API.
 */
function AlertDetailPanel({ alertId }: { alertId: string }) {
  const { data: detail, isLoading, error } = useAlertDetail(alertId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-foreground-muted">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading details...
      </div>
    );
  }

  if (error || !detail) {
    return (
      <p className="text-xs text-foreground-muted py-1">
        Could not load alert details.
      </p>
    );
  }

  const related = detail.relatedEntity as Record<string, unknown> | null;
  if (!related) return null;

  return (
    <div className="mt-2 pt-2 border-t border-border/50">
      <p className="text-xs font-medium text-foreground-muted mb-1">Related Entity</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {related.type === 'SalesOrder' && (
          <>
            {related.gpSalesOrderNumber && (
              <DetailRow label="SO #" value={String(related.gpSalesOrderNumber)} />
            )}
            {related.customerName && (
              <DetailRow label="Customer" value={String(related.customerName)} />
            )}
            {related.status && (
              <DetailRow label="Status" value={String(related.status)} />
            )}
          </>
        )}
        {related.type === 'Task' && (
          <>
            {related.laborBucket && (
              <DetailRow label="Labor Type" value={String(related.laborBucket)} />
            )}
            {related.status && (
              <DetailRow label="Status" value={String(related.status)} />
            )}
          </>
        )}
        {related.type === 'Person' && (
          <>
            {related.name && (
              <DetailRow label="Name" value={String(related.name)} />
            )}
            {related.email && (
              <DetailRow label="Email" value={String(related.email)} />
            )}
          </>
        )}
        {related.type === 'Skill' && (
          <>
            {related.name && (
              <DetailRow label="Skill" value={String(related.name)} />
            )}
            {related.laborBucket && (
              <DetailRow label="Labor Type" value={String(related.laborBucket)} />
            )}
          </>
        )}
        {related.type === 'LineItem' && (
          <>
            {related.status && (
              <DetailRow label="Status" value={String(related.status)} />
            )}
            {related.promisedShipDate && (
              <DetailRow label="Promised" value={safeFormatDate(related.promisedShipDate) ?? String(related.promisedShipDate)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="text-foreground-muted">{label}</span>
      <span className="text-foreground font-medium">{value}</span>
    </>
  );
}

export function AlertCard({
  alert,
  selected = false,
  onSelect,
  onAcknowledge,
  onResolve,
  onDismiss,
}: AlertCardProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const typeInfo = alertTypeInfo[alert.alertType] || {
    label: alert.alertType,
    icon: AlertCircle,
    severity: 'info' as const,
  };
  const Icon = typeInfo.icon;
  const styles = severityStyles[typeInfo.severity];
  const statusStyle = statusStyles[alert.status];

  const contextLines = getAlertContextLines(alert);

  // Determine a navigation link based on alert type
  const getNavigationLink = (): string | null => {
    const p = alert.payload;
    const projectId = payloadStr(p, 'projectId');
    if (projectId) return `/orders/${projectId}`;

    // Fall back to sourceEntityId for SalesOrder-type sources
    if (alert.sourceEntityType === 'SalesOrder') return `/orders/${alert.sourceEntityId}`;
    return null;
  };

  const navLink = getNavigationLink();

  return (
    <div
      className={`rounded-lg border ${styles.border} ${styles.bg} p-4 transition-all ${
        selected ? 'ring-2 ring-primary' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox for selection */}
        {onSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(alert.id)}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
        )}

        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.icon}`}>
          <Icon className="h-5 w-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row: badges + timestamp */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
              {typeInfo.label}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
            </span>
            <span className="text-xs text-foreground-muted">
              {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
            </span>
          </div>

          {/* Human-readable message */}
          <p className="mt-1.5 text-sm text-foreground font-medium">{getAlertMessage(alert)}</p>

          {/* Context lines: key details from payload */}
          {contextLines.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {contextLines.map((line, i) => (
                <span key={i} className="text-xs">
                  <span className="text-foreground-muted">{line.label}:</span>{' '}
                  {line.link ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(line.link!);
                      }}
                      className="font-medium text-primary hover:underline"
                    >
                      {line.value}
                    </button>
                  ) : (
                    <span className="font-medium text-foreground">{line.value}</span>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Acknowledged/Resolved info */}
          {alert.acknowledgedBy && (
            <p className="mt-1.5 text-xs text-foreground-muted">
              Acknowledged by {alert.acknowledgedBy.name}{' '}
              {alert.acknowledgedAt && formatDistanceToNow(new Date(alert.acknowledgedAt), { addSuffix: true })}
            </p>
          )}
          {alert.resolvedBy && (
            <p className="mt-1 text-xs text-foreground-muted">
              Resolved by {alert.resolvedBy.name}
              {alert.resolution && `: ${alert.resolution}`}
            </p>
          )}

          {/* Expandable detail section */}
          {expanded && <AlertDetailPanel alertId={alert.id} />}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Expand/collapse */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-1.5 rounded hover:bg-background-subtle text-foreground-muted hover:text-foreground transition-colors"
            title={expanded ? 'Collapse' : 'Show details'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {/* Navigate to related entity */}
          {navLink && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(navLink);
              }}
              className="p-1.5 rounded hover:bg-background-subtle text-foreground-muted hover:text-primary transition-colors"
              title="View Project"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          )}

          {alert.status === 'active' && onAcknowledge && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAcknowledge(alert.id);
              }}
              className="p-1.5 rounded hover:bg-background-subtle text-foreground-muted hover:text-foreground transition-colors"
              title="Acknowledge"
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          )}
          {(alert.status === 'active' || alert.status === 'acknowledged') && onResolve && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResolve(alert.id);
              }}
              className="p-1.5 rounded hover:bg-background-subtle text-foreground-muted hover:text-success transition-colors"
              title="Resolve"
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          )}
          {onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(alert.id);
              }}
              className="p-1.5 rounded hover:bg-background-subtle text-foreground-muted hover:text-danger transition-colors"
              title="Dismiss"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Summary card for dashboard use
export function AlertSummaryCard({
  counts,
  onClick,
}: {
  counts: { totalActive: number; byType: Record<string, number> };
  onClick?: () => void;
}) {
  const criticalCount =
    (counts.byType.date_slip || 0) +
    (counts.byType.skill_gap || 0) +
    (counts.byType.data_integrity || 0) +
    (counts.byType.explosion_review_required || 0) +
    (counts.byType.memory_pressure || 0);

  return (
    <div
      className={`rounded-lg border p-4 ${
        criticalCount > 0
          ? 'border-danger bg-danger-subtle'
          : counts.totalActive > 0
          ? 'border-warning bg-warning-subtle'
          : 'border-success bg-success-subtle'
      } ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle
          className={`h-6 w-6 ${
            criticalCount > 0 ? 'text-danger' : counts.totalActive > 0 ? 'text-warning' : 'text-success'
          }`}
        />
        <div>
          <p className="text-lg font-semibold text-foreground">
            {counts.totalActive} Active Alert{counts.totalActive !== 1 ? 's' : ''}
          </p>
          {criticalCount > 0 && (
            <p className="text-sm text-danger">{criticalCount} critical</p>
          )}
        </div>
      </div>
    </div>
  );
}
